<template>
  <div
    v-if="loaded"
    :class="{
      'ie': isIE,
      'full-height': isQuestionType(['iframe_q', 'discussion_q', 'file_viewer_q'])
    }"
    class="question-app"
  >

    <assistive-status
      v-if="!interactionMeta.rubric"
      :id="'assist-' + interaction.id"
      :label="assistiveLabel"
      :interactionType="interaction.interactionType"
      ref='assistStatus'
    >
    </assistive-status>

    <div
      tabindex="0"
      class="wrapper"
      :class="{
        'full-height': isQuestionType(['iframe_q', 'file_viewer_q', 'discussion_q', 'secondary_stream']),
        'secondary-stream': isQuestionType('secondary_stream')
      }"
    >
      <v-progress-linear v-if='show_progress' v-model="remaining_time" class='time-progress'></v-progress-linear>

      <LiveSessionInteractionCountdown
        v-if="(
          isLiveSession
          && user.userType === 'student'
        )"
        :interaction="interaction"
      >
      </LiveSessionInteractionCountdown>

      <div
        v-if="!isQuestionType(['iframe_q', 'file_viewer_q', 'secondary_stream'])"
        :id="'interaction-'+interaction.id"
        :class="{
          'full-height': (
            isQuestionType(['iframe_q', 'file_viewer_q', 'discussion_q'])
            || interaction.overlay
          ),
          'portrait': $store.state.orientation === 'portrait',
          'duration': interaction.display_in !== interaction.display_out,
          'discussion': isQuestionType(['discussion_q']),
          'required': (
            interaction.isRequired
            && !interactionMeta.isInteractionAnswered
          ),
          'extra-padding': extraPadding,
        }"
        class="q pa-3 mt-1"
      >
        <!--
          Interaction types:
          - embed
          - file
          - discussion
        -->
        <form
          ref='interactionForm'
          :class="{
            'full-height': isQuestionType(['iframe_q', 'file_viewer_q', 'discussion_q'])
          }"
          @submit.prevent
        >
          <fieldset :class="{'full-height': isQuestionType(['iframe_q', 'discussion_q'])}">
            <legend class="q-legend" v-if="!isQuestionType(['iframe_q', 'discussion_q'])">
              <interaction-pause v-if="isQuestionType('pause_q')" :interaction-id="interactionId" :visible="visible" class="q-header"></interaction-pause>
              <interaction-short-answer v-else-if="isQuestionType('short_answer')" :interaction-id="interactionId" :visible="visible" class="q-header"></interaction-short-answer>
              <div v-else :key="interactionTitleKey" class="pp-interaction-title" role="heading" aria-level="2" v-html="interaction.interactionText" :class="{ 'white--text': interactionMeta.darkMode }"></div>
              <div v-if="multiSelect" role="subheader" class="subtitle-1 font-weight-light" :class="interactionMeta.darkMode ? 'white--text' : ''">(check all that apply)</div>
              <interaction-attachments :attachments="interaction.interactionAttachments" />
            </legend>
            <div class="q-main" :class="{'full-height': isQuestionType(['iframe_q', 'discussion_q'])}">
              <interaction-radio
                v-if="singleSelect"
                class="q-radio"
                :interaction-id="interactionId"
                :visible="visible"
                :option-count="broadcastOptionsCounts">
              </interaction-radio>
              <interaction-checkbox
                v-if="multiSelect"
                class="q-checkbox"
                :interaction-id="interactionId"
                :visible="visible"
                :option-count="broadcastOptionsCounts">
              </interaction-checkbox>
              <interaction-textarea
                v-if="isQuestionType('free_response')"
                class="q-response"
                :interaction-id="interactionId"
                :visible="visible">
              </interaction-textarea>
              <interaction-discussion
                v-if="isQuestionType('discussion_q')"
                class="q-discussion full-height"
                :interaction-id="interactionId"
                :visible="visible"
                discussion_type="forum">
              </interaction-discussion>
            </div>
            <interaction-explanation v-if="showExplanation" :interaction-id="interactionId"></interaction-explanation>
          </fieldset>
        </form>

        <!--
          Interaction types:
          - mc
          - checkall
          - poll
          - fib
          etc...
        -->
        <div
          v-if="(
            !isQuestionType(['discussion_q', 'file_viewer_q', 'pause_q', 'secondary_stream'])
            && !interactionMeta.rubric
          )"
          :class="{
            'mobile': mobile,
          }"
        >
          <interaction-score v-if="interactionMeta.isInteractionAnswered" :interaction='interaction' :interactionAttempt="interactionAttempt" :interactionMeta="interactionMeta"></interaction-score>
          <broadcast-pin :interactionId="interactionId"></broadcast-pin>
          <div class="text-xs-right interaction-buttons">
            <div :class="freeResponseClassObject">
              <interaction-submit
                v-if="!showRetakeButton"
                :interaction-id="interactionId"
                class="v-submit">
              </interaction-submit>            
              <free-response-edit
                v-if="isQuestionType(['free_response']) && hasFreeResponseEdit"
                :interaction-id="interactionId">
              </free-response-edit>
              <interaction-retake
              v-if="showRetakeButton"
              v-show="interactionMeta.isInteractionAnswered"
              :interaction-id="interactionId"
              class="v-retake">
              </interaction-retake>
              <interaction-continue-menu
                ref="interactionContinueMenu"
                v-if="
                (
                  interactionMeta.isInteractionAnswered
                  || interactionMeta.isRetakeExplanation
                )
                &&
                (
                  !isBroadcast
                  || broadcastTeacher
                ) || isLiveSession"
                :interaction-id="interactionId"
                v-show="interactionMeta.isInteractionAnswered">
              </interaction-continue-menu>
            </div>
          </div>
        </div>

        <!--
          Broadcast Pin for interaction types:
          - Discussion
          - Pause
          - File Viewer
        -->
        <div
          v-if="(
            isQuestionType(['discussion_q', 'file_viewer_q', 'pause_q'])
            && !interactionMeta.rubric
          )"
          :class="{
            'mobile': mobile,
          }"
        >
          <broadcast-pin
            :interactionId="interactionId"
            :isDiscussionType="isQuestionType(['discussion_q'])"
          >
          </broadcast-pin>
        </div>

        <!-- pause continue button -->
        <interaction-continue-menu
          v-if="enablePauseContinueMenu"
          :interaction-id="interactionId"
          floatAlign="right">
        </interaction-continue-menu>

        <liveSessionAnalytics
          v-if="(
            isLiveSession
            && interactionMeta.pastSessionTime
            && ['multiple_choice', 'check_all_apply', 'poll_q'].includes(interaction.interactionType)
          )"
          :darkMode="interaction.position.dark"
          :interactionId="interactionId"
          :sectionId="bulbAttempt.sectionId"
          :visible="visible"
        >
        </liveSessionAnalytics>
      </div>

      <interaction-embed
        v-if="isQuestionType('iframe_q')"
        :interaction-id="interactionId"
        :visible="visible"
        class="q-embed"
      >
      </interaction-embed>
      <InteractionFileViewer
        v-if="isQuestionType('file_viewer_q')"
        :interaction-id="interactionId"
        :visible="visible"
        class="q-file-viewer"
      >
      </InteractionFileViewer>
      <interaction-video
        v-if="interaction.interactionType === 'secondary_stream'"
        :interaction="interaction"
        :interactionMeta="interactionMeta"
        :visible="visible"
      >
      </interaction-video>
    </div>
    
    <!-- Teacher Broadcast shennans: -->
    <div
      v-if="broadcastTeacher"
      class="broadcast__learner-attempts"
    >
      <v-layout
        v-if="!isQuestionType(['discussion_q'])"
        class="mt-2"
      >
        <v-container>
          <v-layout row>
            <v-btn
              v-if="!broadcastHideResponses"
              small
              class="floating-btn theme--light primary--text"
              id="list-responses"
              role='button'
              aria-label='Show Responses'
              :loading="broadcastResponsesLoading"
              @click='broadcastShowLearnerResponses' >
                Show Responses ({{learnerAttemptsCount}})
            </v-btn>
            <v-btn
              v-else
              small
              class="floating-btn theme--light primary--text"
              id="list-responses"
              role='button'
              aria-label='Hide Responses'
              @click='broadcastHideResponses = showNames = false' >
                Hide Responses ({{learnerAttemptsCount}})
            </v-btn>
            <v-btn
              v-if="showBroadcastAnalyticsButton"
              small
              class="floating-btn theme--light primary--text"
              id="analyticsButton"
              role='button'
              :aria-label="analyticsButtonAriaLabel"
              :loading="broadcastAnalyticsLoading"
              @click='handleBroadcastAnalyticsButtonClicked(false)'>
                {{ this.showBroadcastAnalytics ? 'Hide Analytics' : 'Show Analytics' }}
            </v-btn>
            <Tooltip
              v-if="showBroadcastAnalytics"
              top>
              <v-btn
                flat
                icon
                small
                aria-label="Refresh analytics"
                class="theme--light primary--text"
                @click="handleBroadcastAnalyticsButtonClicked(true)">
                <v-icon>refresh</v-icon>
              </v-btn>
              <template v-slot:text>
                Refresh analytics
              </template>
            </Tooltip>
          </v-layout>
          <v-layout row>
            <v-flex>
              <v-switch
                v-if="broadcastHideResponses"
                v-model="showNames"
                label="Show names"
                aria-label='Show Names'>
              </v-switch>
            </v-flex>
          </v-layout>
        </v-container>
      </v-layout>
      <v-list
        v-if="(
          !broadcastResponsesLoading
          && !isQuestionType(['discussion_q'])
        )"
        three-line v-show="broadcastHideResponses"
      >
        <v-subheader inset>Student Responses</v-subheader>
        <template v-for="(response, index) in broadcastLearnerResponses">
          <v-list-tile class="auto-height-list-tile" :key="response.id">
            <v-list-tile-content>
              <div class="v-list-tile-title" v-if="showNames">{{broadcastGetLearnerName(response.bulbAttemptId)}}</div>
              <div v-if="activeInteraction.interactionType != 'short_answer'"class="v-list-tile-subtitle" v-html='response.responseText.replace(/\,/g,"")'></div>
              <div v-if="activeInteraction.interactionType === 'short_answer'" v-for="(text, textIndex) in broadcastGetResponseTextArray(response.responseText)">
                <div class="v-list-tile-title sort-answer-response-text"> Blank {{textIndex + 1}} </div> {{text}}
              </div>
            </v-list-tile-content>
          </v-list-tile>
          <v-divider :key="index"></v-divider>
        </template>
        <v-container id="broadcast-pagination">
          <el-pagination
            small
            id="pagination"
            layout="sizes, prev, pager, next"
            :current-page.sync="broadcastResponsesPage"
            :page-sizes="rowsPerPage"
            :page-size="broadcastResponsesPerPage"
            :disabled="broadcastResponsesLoading"
            :total="brodcastResponsesTotal"
            @prev-click="pageChange"
            @next-click="pageChange"
            @current-change="pageChange"
            @size-change="sizeChange"
            >
          </el-pagination>
        </v-container>
      </v-list>
      <v-container
        v-if="(
          broadcastResponsesLoading
          && broadcastHideResponses
          && !isQuestionType(['discussion_q'])
        )"
        class="loader">
        <v-progress-circular indeterminate size=70 :width="5"></v-progress-circular>
      </v-container>
      <BarChart
        v-if="(
          showBroadcastAnalytics
          && !broadcastAnalyticsLoading
        )"
        :key="`broadcast-barchart-${interactionId}`"
        :chart-data="chartData"
        :options="options"
        :styles="barChartStyle"
        role="alert"
      ></BarChart>
    </div>

  </div>
</template>

<script>
/* global _ */
/* global moment */

// Core:
import {
  mapGetters,
  mapMutations,
  mapActions,
  mapState,
} from 'vuex';

// Libraries / Plugins:
import EventBus from '@/buses/EventBus';
import _ from 'lodash';

// Custom Services:
import PlayerService from '@/services/PlayerService';
import XApiService from '@/services/XApiService';

// Utilities:
import { waitForElementToDisplay } from '@/js/helpers.js';

import BroadcastPin from '@/components/interaction/BroadcastPin';
import InteractionPause from '@/components/interaction/InteractionPause';
import InteractionRadio from '@/components/interaction/InteractionRadio';
import InteractionCheckbox from '@/components/interaction/InteractionCheckbox';
import InteractionTextarea from '@/components/interaction/InteractionTextarea';
import InteractionEmbed from '@/components/interaction/InteractionEmbed';
import InteractionFileViewer from '@/components/interaction/InteractionFileViewer';
import InteractionExplanation from '@/components/interaction/InteractionExplanation';
import InteractionScore from '@/components/interaction/InteractionScore';
import InteractionShortAnswer from '@/components/interaction/InteractionShortAnswer';
import InteractionDiscussion from '@/components/interaction/discussion/InteractionDiscussion';
import InteractionVideo from '@/components/interaction/InteractionVideo';
import LiveSessionAnalytics from '@/components/interaction/liveSessionAnalytics/LiveSessionAnalytics'
import LiveSessionInteractionCountdown from '@/components/interaction/LiveSessionInteractionCountdown';
import QuestionModal from '@/components/interaction/QuestionModal';

import InteractionAttachments from '@/components/interaction/InteractionAttachments';
import InteractionSubmit from '@/components/interaction/InteractionSubmit';
import InteractionContinueMenu from '@/components/interaction/InteractionContinueMenu';
import InteractionRetake from '@/components/interaction/InteractionRetake';
import AssistiveStatus from '@/components/AssistiveStatus';
import BarChart from './broadcastAnalytics/BarChart';
import FreeResponseEdit from '@/components/interaction/freeResponse/FreeResponseEdit'

import { triggerLearnerAttemptStatus } from '@/js/firestore-utils';

export default {
  name: 'interaction',
  components: {
    AssistiveStatus,
    BarChart,
    BroadcastPin,
    InteractionExplanation,
    InteractionPause,
    InteractionRadio,
    InteractionScore,
    InteractionSubmit,
    InteractionContinueMenu,
    InteractionRetake,
    InteractionCheckbox,
    InteractionTextarea,
    InteractionEmbed,
    InteractionFileViewer,
    InteractionShortAnswer,
    InteractionDiscussion,
    InteractionVideo,
    InteractionAttachments,
    LiveSessionAnalytics,
    LiveSessionInteractionCountdown,
    QuestionModal,
    FreeResponseEdit,
  },
  props: [
    'frame_id',
    'interactionId',
    'showScrollToBottom', // when we get rid of interaction wrapper, make this one a property
    'visible',
  ],
  data() {
    return {
      assistiveLabel: 'Press Tab to start interaction.',
      chartData: {},
      loaded: false,
      previous_pulse_ids: [],
      interactionIndex: -1,
      interactionTitleKey: 1,
      learnerAttemptsCount: 0,
      broadcastAnalyticsLoading: false,
      broadcastHideResponses: false,
      broadcastResponsesLoading: false,
      broadcastLearnerResponses: [],
      broadcastOptionsCounts: null,
      instantJump: null,
      showNames: false,
      tocInteractionId: null,
      broadcastResponsesPerPage: 10,
      broadcastResponsesPage: 0,
      brodcastResponsesTotal: 0,
      options: {
        title: {
          display: false, 
          position: 'top',
          text: `Session Answer Summary`,
        },
        tooltips: {
          callbacks: {
            title: function (tooltipItems, data) {
                return data.labels[tooltipItems[0].index]
            }
          }
        },
        hover: {mode: null},
        scales: {
          xAxes: [
            {
              display: true,
              legend: {
                display: true,
              },
              ticks: {
                callback: function(tick) {
                  const characterLimit = 30;
                  if (tick.length >= characterLimit) {
                    return tick.slice(0, tick.length).substring(0, characterLimit -1).trim() + '...';
                  } 
                  return tick;
                },
                min: 0,
                maxRotation: 45,
                minRotation: 45,
              },
              maxBarThickness: 20,
            }
          ],
          yAxes: [
            {
              gridLines: { 
                display: false,
              },
              ticks: {
                beginAtZero: true,
                fontColor: this.interaction?.position?.dark ? 'white' : '#000',
                fontSize: 15,
                precision: 0,
              },
            }
          ]
        },
        layout: {
          padding: {
            // This padding is needed to allow for dataLabels to not be cutoff
            right: 48,
          }
        },
      },
      showBroadcastAnalytics: false,
    }
  },
  computed: {
    ...mapState('player', [
      'isLiveSession',
      'setFirstFocus',
      'delimiter',
    ]),
    ...mapState('interactions', [
      'editFreeResponseState'
    ]),
    ...mapGetters('player', [
      'bulb',
      'bulbAttempt',
      'displayTime',
      'interactionAttempts',
      'mobile',
      'screenRatio',
      'sidebarStyle',
      'user',
      'broadcastId',
      'broadcastLearners',
      'broadcastTeacher',
      'isBroadcast',
      'hasFreeResponseEdit',
    ]),
    ...mapGetters('interactions', [
			'activeInteraction',
			'activeInteractionGroup',
			'activeInteractionMeta',
      'interactions',
      'requiredInteractionIndex',
    ]),
    delimiter() {
      return this?.interaction?.config?.hasNewFillInTheBlankDelimiter
        ? '^||*|||*||^'
        : ',';
    },
    interactionAttempts() {
      return this.interactionMeta.attempts;
    },
    analyticsButtonAriaLabel() {
      return this.showBroadcastAnalytics ? 'Hide analytics' : 'Show analytics';
    },
    barChartStyle() {
      return {
        position: 'relative',
        width: '100%',
        'margin-bottom': '40px',
      };
    },
    enablePauseContinueMenu() {
      return (
        this.isQuestionType('pause_q')
        && (
          !this.isBroadcast
          || this.broadcastTeacher
        )
        && !this.interactionMeta.rubric
      );
    },
    interaction() {
      return this.$store.getters['interactions/interaction'](this.interactionId) || {};
    },
    interactionAttempt() {
      return this.$store.getters['interactions/interactionAttempt'](this.interactionId) || {};
    },
    interactionMeta() {
      return this.$store.getters['interactions/interactionMeta'](this.interactionId) || {};
    },
    isIE() {
      return !!window.MSInputMethodContext && !!document.documentMode;
    },
    extraPadding() {
      if (
        this.interactionMeta.overlay 
        && this.screenRatio > 1.5 
        && !this.isQuestionType(['iframe_q', 'secondary_stream'])) {
        const top = parseFloat(this.interaction.position.top.replace('%', ''));
        const height = parseFloat(this.interaction.position.height.replace('%', ''));
        return top + height >= 90 || this.interactionMeta.expanded;
      }
      return false;
    },
    freeResponseClassObject() {
      return {
        'free-response-unanswered': !this.interactionMeta.isInteractionAnswered,
      };
    },
    hiddenByRequired() {
      for (const is of this.activeInteractionGroup) {
        if (is.interaction.id === this.interaction.id) return false;
        if (
          is.interaction.isRequired
          && !is.interactionMeta.isInteractionAnswered
          && !is.interactionMeta.rubric
          && is.interaction.displayOutTime < this.interaction.displayInTime
        ) return true;
      }
      return false;
    },
    singleSelect() {
      return this.isQuestionType('multiple_choice') || (this.isQuestionType('poll_q') && !this.interaction.config.multiselect);
    },
    multiSelect() {
      return this.isQuestionType('check_all_apply') || (this.isQuestionType('poll_q') && this.interaction.config.multiselect);
    },
    showExplanation() {
      return this.interactionMeta.rubric && !this.isQuestionType(['pause_q', 'discussion_q', 'iframe_q']);
    },
    showBroadcastAnalyticsButton() {
      return (
          !this.isQuestionType(['discussion_q', 'pause_q', 'secondary_stream', 'iframe_q', 'short_answer', 'free_response', 'file_viewer_q'])
        );
    },
    showRetakeButton() {
      /**
       * See if retake button should be shown to end user.
       */
      if (this.isLiveSession) return false;
      const canRetake = (this.interactionMeta.isInteractionAutograded && !this.interactionMeta.isAnswerCorrect) || ['poll_q', 'free_response'].includes(this.interaction.interactionType);
      return this.interactionMeta.isInteractionAnswered && canRetake && (this.interactionMeta.attempts < this.interaction.attempts || this.interaction.attempts === 0);
    },
    show_progress() {
      return this.interaction.display_out - this.interaction.display_in > .1;
    },
    remaining_time() {
      return Math.max((this.displayTime - this.interaction.display_in) / (this.interaction.display_out - this.interaction.display_in) * 100, 0);
    },
    rowsPerPage: function(){
      let rowsPerPage = [];
      if(this.brodcastResponsesTotal <= 10) rowsPerPage = [this.brodcastResponsesTotal];
      if(this.brodcastResponsesTotal > 10 && this.brodcastResponsesTotal <= 20) rowsPerPage = [10, this.brodcastResponsesTotal];
      if(this.brodcastResponsesTotal > 20 && this.brodcastResponsesTotal <= 30) rowsPerPage = [10, 20, this.brodcastResponsesTotal];
      if(this.brodcastResponsesTotal > 30 && this.brodcastResponsesTotal <= 40) rowsPerPage = [10, 20, 30, this.brodcastResponsesTotal];
      if(this.brodcastResponsesTotal > 40 && this.brodcastResponsesTotal <= 50) rowsPerPage = [10, 20, 30, 40, this.brodcastResponsesTotal];
      if(this.brodcastResponsesTotal > 50) rowsPerPage = [10, 20, 30, 40, 50];
      return rowsPerPage;
    }
  },
  methods: {
    ...mapMutations('player', [
      'setDesiredState',
      'setSnackbar',
    ]),
    ...mapActions('interactions', [
      'requestSetActiveInteraction',
    ]),
    log() {
      console.log('interaction', this.interaction);
      console.log('interactionAttempt', this.interactionAttempt);
      console.log('interactionMeta', this.interactionMeta);
    },
    isQuestionType(keys) {
      if (!this.interaction) return false;
      
      return keys.includes(this.interaction.interactionType);
    },
    async handleBroadcastAnalyticsButtonClicked(refresh) {
      if (!refresh) this.showBroadcastAnalytics = !this.showBroadcastAnalytics;
      this.broadcastAnalyticsLoading = true;
      const { data: graphData } = await PlayerService.getBroadcastAnalyticsData({
        interactionId: this.interactionId,
        sectionId: this.bulbAttempt.sectionId,
      });
      this.chartData = graphData;
      this.broadcastAnalyticsLoading = false;
    },
    handleDataPpJumpEvents(e) {
      if (
        e instanceof (KeyboardEvent)
        && !['enter', 'space'].includes(e.code.toLowerCase())
      ) return;
      const dt = e.target.getAttribute('data-pp-jump');
      EventBus.$emit('requestAction', {
        key: 'seek',
        seekTime: dt,
        bypassGates: ['notWithinBounds', 'alreadySeeking']
      });
    },
    handleDataTocInteractionEvents(e) {
      const element = e.target.parentNode;
      const dt = element.getAttribute('data-toc-jump');
      const requestedInteractionId = parseInt(element.getAttribute('data-toc-interaction'), 10);
      const requestedInteraction = _.find(this.interactions, { 'id': requestedInteractionId});
      EventBus.$emit('requestAction', {
        key: 'seek',
        seekTime: dt,
        bypassGates: ['notWithinBounds', 'alreadySeeking']
      });
      setTimeout(() => { this.requestSetActiveInteraction(requestedInteraction); }, 5);
    },
    setupClickListeners() {
      // Disable click events for students taking a broadcast.  All jumps can be triggered byt the teacher.
      if (
        this.isLiveSession
        || (
          this.isBroadcast
          && this.user.userType === 'student'
        )
      ) return;
      waitForElementToDisplay(`#interaction-${this.interaction.id} [data-pp-jump]`, (jumps) => {
        if (jumps) {
          Array.from(jumps).forEach((e) => {
            e.style.pointerEvents = 'auto'; // This is needed to allow learners to click jump buttons
            e.removeEventListener('click', this.handleDataPpJumpEvents);
            e.addEventListener('click', this.handleDataPpJumpEvents);
            e.addEventListener('keyup', this.handleDataPpJumpEvents);
          });
        }
      }, 5);
      waitForElementToDisplay(`#interaction-${this.interaction.id} [data-pp-jump-instant]`, (jumps) => {
        if (jumps) {
          Array.from(jumps).forEach((e) => {
            const dt = e.getAttribute('data-pp-jump-instant');
            this.instantJump = dt;
          });
        }
      }, 5);
      waitForElementToDisplay(`#interaction-${this.interaction.id} [data-toc-interaction]`, (jumps) => {
        if (jumps) {
          Array.from(jumps).forEach((e) => {
            e.removeEventListener('click', this.handleDataTocInteractionEvents);
            e.addEventListener('click', this.handleDataTocInteractionEvents);
          });
        }
      }, 5);
      if (this.interaction.interactionType === 'short_answer') {
        /**                                                                                                                       ┈┈╱▔▔▔▔▔╲┈┈┈??????????
         *                                                                                                                        ┈╱┈┈╱▔╲╲╲▏┈┈┈?????┈
         *  How we add jumps is silly.  Once you reckon with that here is a brief explanation of the recursive nightmare below... ╱┈┈╱━╱▔▔▔▔▔╲━╮┈┈
         *  We have to wait for the feedback element to display before we can add event listeners to it.  Due to how we store     ▏┈▕┃▕╱▔╲╱▔╲▕╮┃┈┈
         *  that jump info ([data-pp-jump]) on fill in the blank interactions we have to double wait for the feedback element     ▏┈▕╰━▏▊▕▕▋▕▕━╯┈┈
         *  AND THEN wait for the jump button (span/div) element.  Once that happens (after a user submits an answer) we can      ╲┈┈╲╱▔╭╮▔▔┳╲╲┈┈┈
         *  add event listeners like normal.                                                                                      ┈╲┈┈▏╭━━━━╯▕▕┈┈┈
         *                                                                                                                        ┈┈╲┈╲▂▂▂▂▂▂╱╱┈┈┈
        */
        waitForElementToDisplay(`#interaction-${this.interaction.id}`, (element) => {
          waitForElementToDisplay('span [data-pp-jump]', (jumps) => {
            if (jumps) {
              Array.from(jumps).forEach((e) => {
                e.removeEventListener('click', this.handleDataPpJumpEvents);
                e.addEventListener('click', this.handleDataPpJumpEvents);
                e.addEventListener('keyup', this.handleDataPpJumpEvents);
              });
            }
          }, 5, 0, element[0]);
        }, 5);
      }
    },
    buildSubmitQuestionStatement(question_id) {
      /**
       * Method to create a submission statement for the current question:
       * Creates submission object for the question
       * For autograded questions: create submission response with points earned out of points total
       * For not autograded questions: creates generic submission response.
      */
      if (this.interaction.id === question_id) {
        var submission = {
          activity: {
            id: `https://xapi.playposit.com/activities/question/${this.interaction.id}`,
            name: this.interaction.interactionType,
            question_id: this.interaction.id
          },
        }
        if (_.isNumber(this.interactionAttempt.pointsEarned)) {
          // Autograded
          submission.result = {
            score: {
              raw: (this.interactionAttempt.pointsEarned > this.interactionAttempt.pointsAvailable) ? this.interaction.points_available : this.interactionAttempt.pointsEarned,
              min: 0,
              max: this.interaction.points_available
            },
            success: (this.interactionAttempt.pointsEarned === this.interactionAttempt.pointsAvailable),
            completion: true,
            response: `${XApiService.defaultStatement.actor.name} submitted an answer to question #${this.interaction.id} for ${this.interactionAttempt.pointsEarned} ${this.interactionAttempt.pointsEarned > 1 ? 'points' : 'point'} out of a total of ${this.interactionAttempt.pointsAvailable} ${this.interactionAttempt.pointsAvailable > 1 ? 'points' : 'point'}.`
          }
        } else {
          // Not autograded
          submission.result = {
            success: true,
            completion: true,
            response: `${XApiService.defaultStatement.actor.name} submitted an answer to question #${this.interaction.id}.`
          }
        }
        XApiService.buildSubmitQuestionStatement(submission);

        if (this.isBroadcast && this.user.userType === 'student') {
          // @Ankit, is this change correct?
          // -- trigger leaner update status for broadcast
          triggerLearnerAttemptStatus(this.broadcastId, this.user.id, this.interaction.id);
        }
      }
    },
    sessionTime(){
      console.log(moment.duration(moment().diff(this.interactionMeta.sessionStartTime)).asSeconds() + this.interactionMeta.tempSessionTime)
    },
    broadcastShowLearnerResponses() {
      // if(!this.broadcastHideResponses) {
        const bulbAttemptIds = _.map(this.broadcastLearners, 'data.bulbAttemptId');
        this.broadcastResponsesLoading = true;
        PlayerService.getBroadcastInteractionAttemptDetails(this.interaction.id, bulbAttemptIds.join(','), this.broadcastResponsesPage, this.broadcastResponsesPerPage)
        .then(response => {
          this.broadcastResponsesLoading = false;
          if(response.status === 200 && response.data && response.data.error === null) {
            this.broadcastLearnerResponses = response.data.data.rows;
            this.brodcastResponsesTotal = response.data.data.count;
            this.broadcastHideResponses = true;
          }
          else dataLayer.errorLog(response, 'Interaction:broadcastFetchResponses');
        }).catch(err => {
          this.broadcastResponsesLoading = false;
        });
      // }
    },
    broadcastGetLearnerName(bulbAttemptId) {
      const learner = _.filter(this.broadcastLearners, data => (data.data && data.data.bulbAttemptId === bulbAttemptId));
      if(learner) return `${learner[0].learnerDetails.firstName} ${learner[0].learnerDetails.lastName}`;
      else 'Inactive user';
    },
    broadcastGetResponseTextArray(responseText) {
      if(!responseText) return [];
      return responseText.split(this.delimiter);
    },
    pageChange: function(event){
      this.broadcastResponsesPage = event;
      this.broadcastShowLearnerResponses();
    },
    sizeChange: function(event){
      this.broadcastResponsesPerPage = event;
      if( event === this.brodcastResponsesTotal ) this.broadcastResponsesPage = 1;
      this.broadcastShowLearnerResponses();
    },
  },
  watch: {
    'activeInteraction'(n, o) {
      if (n && n.id === this.interaction.id && this.$refs.assistStatus) {
        this.$refs.assistStatus.$el.focus();
      }
      if (n && n.id === this.interaction.id && this.interactionAttempt.timeElapsed === 0 && !this.interactionMeta.sessionStartTime) {
        this.interactionMeta.sessionStartTime = moment()
      }
      else if (o && o.id === this.interaction.id && this.interactionAttempt.timeElapsed === 0) {
        this.interactionMeta.tempSessionTime += moment.duration(moment().diff(this.interactionMeta.sessionStartTime)).asSeconds()
        this.sessionStartTime = null;
      }
    },
    'hiddenByRequired'(){
      this.$set(this.interactionMeta, 'hiddenByRequired', this.hiddenByRequired);
    },
    'visible'() {
      if (this.visible) this.setupClickListeners();
      if (!this.visible && ['pause_q', 'iframe_q', 'discussion_q'].includes(this.interaction.interactionType) && this.interactionMeta.isInteractionAnswered && this.interactionAttempt.timeElapsed < .1) {
        // submit interactionAttempt to update time elapsed
        this.interactionAttempt.timeElapsed = moment.duration(moment().diff(this.interactionMeta.sessionStartTime)).asSeconds() + this.interactionMeta.tempSessionTime;
        if (this.user.userType === 'student') {
          PlayerService.submitExplanation({
            interaction: this.interaction,
            interactionAttempt:this.interactionAttempt,
            bulbAttempt: this.bulbAttempt,
          });
        }
      } else if (this.visible && this.instantJump) {
        this.setSnackbar({
          text: `Autojumping to ${new Date(this.instantJump * 1000).toISOString().substr(11, 8)}`,
          color: 'warning',
          class: 'data-pp-jump-instant-toast',
        });
        EventBus.$emit('requestAction', {
          key: 'seek',
          seekTime: this.instantJump,
          bypassGates: ['notWithinBounds', 'alreadySeeking']
        });
      }
      /*
        DUCTTAPE: for phet.colorado.edu/physnav.org simulations
        phet/physnav sites attempt to get width of their container, however interactions are
        hidden until they pop, so width check fails making the phet site fail to load.
        Cycling the key refreshes the interaction title and reloads the embedded phet iFrame
      */
      if (
        this.visible
        && (
          this.interaction.interactionText.includes('phet.colorado.edu')
          || this.interaction.interactionText.includes('physnav.org')
          || this.interaction.interactionText.includes('h5p.com')
        )
      ) {
        this.interactionTitleKey += 1;
      }
      if (this.isLiveSession && this.visible && !this.interactionMeta.viewed) this.interactionMeta.viewed = true;
    },
    'broadcastLearners'() {
      // if(this.activeInteraction && this.activeInteraction.id === this.interaction.id) {
        const learnerAttempts = _.filter(this.broadcastLearners, (item) => {
          return (item.data && item.data[this.interaction.id+''] !== undefined && item.data[this.interaction.id+''] !== null)
        });
        this.learnerAttemptsCount = learnerAttempts && learnerAttempts.length || 0;
      // }
    },
    'broadcastLearnerResponses'() {
      let interactionids = _.map(this.interaction.interactionOptions, 'id');
      interactionids = _.map(interactionids, id => {
        const optionCount = _.filter(this.broadcastLearnerResponses, o => {
          return o.interactionOptionIds && o.interactionOptionIds.indexOf(id) > -1;
        });
        const oc = {};
        oc[id] = optionCount && optionCount.length || 0;
        return oc;
      });

      interactionids = _.reduce(interactionids, function(memo, current) { return _.assign(memo, current) },  {})
      this.broadcastOptionsCounts = {...interactionids};
    },
    'interactions'() {
      this.interactionIndex = _.findIndex(this.interactions, this.interaction);
    },
    interactionAttempts(newValue) {
      if (
        this.interaction.interactionType === 'short_answer'
        && newValue > 0
      ) this.setupClickListeners();
    }
  },
  mounted() {
    this.setupClickListeners();
    this.loaded = true;
    this.assistiveLabel = `${this.interactionMeta.overlay ? 'hotspot' : 'sidebar' } interaction. Press tab to begin.`
    this.interactionIndex = _.findIndex(this.interactions, this.interaction);
    this.$nextTick(()=> {
      this.$set(this.interactionMeta, 'hiddenByRequired', this.hiddenByRequired);
    });
    EventBus.$on('buildSubmitQuestionStatement', question_id => this.buildSubmitQuestionStatement(question_id));
    EventBus.$on('retakeInteraction', async (interactionId) => {
      if (interactionId === this.interaction.id) {
        await this.$nextTick();
        this.setupClickListeners();
      }
    });

    if(this.isBroadcast && (this.user && this.user.userType === 'teacher')) {
      // if(this.activeInteraction && this.activeInteraction.id === this.interaction.id) {
      const learnerAttempts = _.filter(this.broadcastLearners, (item) => {
        return (item.data && item.data[this.interaction.id+''] !== undefined && item.data[this.interaction.id+''] !== null)
      });
      this.learnerAttemptsCount = learnerAttempts && learnerAttempts.length || 0;
      // }
    }
  },
};
</script>

<style>

  .interaction.secondaryStream {
    overflow: hidden;
  }

  .interaction p {
    margin: 0;
    word-break: break-word;
  }

  .interaction p::after {
    content: '';
    display: block;
    clear: both;
  }

  .question-app .icon {
    display: inline-flex !important;
  }

  .question-app.ie .input-group__input {
    left: 0;
  }

  .application.theme--light {
    background-color: transparent;
  }

  .question-app br[data-mce-bogus="1"],
  .question-app .mce-branding {
    display:none !important;
  }

  .question-app .application--wrap {
    min-height: 0;
    width: 100%;
  }

  .question-app {
    background: transparent;
    flex-grow: 1;
    width: 100%;
    position: relative;
  }

  .hotspot .question-app {
    display: flex;
    margin: auto;
  }

  .hotspot .question-app .discussion {
    padding-bottom: 25px;
  }

  .question-app fieldset {
    border: 0;
    min-width: 0;
  }

  .question-app .hidden {
    position: absolute;
    left: -9999999999999999px;
  }

  .question-app .toolbar {
    box-shadow: none !important;
  }

  .question-app .full-height {
    height: 100%;
    flex-grow: 1;
  }

  .question-app .relative {
    position: relative;
  }

  .question-app .processing {
    pointer-events: none;
    user-select: none;
  }

  .question-app .content {
    height: 100%;
  }

  .question-app .content--wrap {
    max-height: 100%;
    height: 100%;
  }

  .question-app .btn {
    margin-left: 0;
    margin-right: 0;
  }

  .question-app .input-group {
    padding: 0;
  }

  .question-app .embed-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    width: 100%;
    -webkit-overflow-scrolling: touch; /* allow touch scrolling  */
 	  overflow: auto;
  }

  .question-app .embedded-iframe {
    border: 0;
    display: block;
  }

  .question-app *[data-pp-jump],
  .question-app *[data-ppmodal] {
    cursor: pointer;
  }

  .question-app .wrapper {
    position: relative;
    width: 100%;
  }

  .wrapper.secondary-stream {
    display: flex;
  }

  .question-app a {
    cursor: pointer !important;
  }

  .question-app .q {
    overflow-y: hidden;
    width: 100%;
    border-radius: 2px;
  }

  .extra-padding {
    padding-bottom: 75px !important;
  }
  @media screen and (min-width: 0px) and (max-width: 480px) { 
    .extra-padding {
      padding-bottom: 55px !important;
    }
  }
  .question-container .v-expansion-panel__header, .hotspot .v-expansion-panel__header {
    padding: 16px !important;
  }

  .question-app .q.duration {
    border-radius: 0px 0px 2px 2px;
  }

  .question-app .q-legend {
    /*margin-bottom: 1em;*/
    width: 100%;
    display: inline-block;
  }
  .question-app legend {
    min-width: 100%;
  }

  .question-app .radio-option,
  .question-app .checkbox-option,
  .short-answer-feedback,
  .free-response-feedback{
    padding: 5px;
  }
  .radio-option audio, .checkbox-option {
    min-width: 220px;
  }

  .question-app .v-input--selection-controls{
    margin-top: 0;
    padding-top: 0;
  }
  .question-app .v-input--selection-controls .v-input__slot {
    margin-bottom: 0;
  }
  .interaction .v-input--selection-controls .v-input__control {
    flex-grow: 1;
    width: inherit;
  }
  .question-app .radio-option.correct,
  .checkbox-option.correct,
  .short-answer-feedback.correct {
    background-color: #e6f4ea!important;
  }
  .question-app .correct .feedback-blockquote {
    border-left: 5px solid #4CAF50;
  }
  .question-app .transparent-mode-correct .feedback-blockquote {
    border-left: 5px solid #4CAF50;
  }
  .question-app .radio-option.incorrect,
  .checkbox-option.incorrect,
  .short-answer-feedback.incorrect {
    background-color: #fce8e6!important;
  }
  .question-app .incorrect .feedback-blockquote {
    border-left: 5px solid #F44336;
  }
  .question-app .transparent-mode-incorrect .feedback-blockquote {
    border-left: 5px solid #F44336;
  }
  .question-app .radio-option.missed, .checkbox-option.missed, .free-response-feedback.missed {
    background-color: #fef7e0!important
  }
  .question-app .missed .feedback-blockquote {
    border-left: 5px solid #FFC107;
  }
  .question-app .dark-mode-missed .feedback-blockquote {
    border-left: 5px solid #FFC107;
  }
  .question-app .dark-mode-correct .feedback-blockquote {
    border-left: 5px solid #4CAF50;
  }
  .question-app .dark-mode-incorrect .feedback-blockquote {
    border-left: 5px solid #F44336;
  }
  .question-app .radio-option.selected, .checkbox-option.selected {
    background-color: #E3F2FD !important;
  }
  .question-app .selected .feedback-blockquote {
    border-left: 5px solid #2196F3;
  }
  .question-app .transparent-mode-selected .feedback-blockquote {
    border-left: 5px solid #2196F3;
  }
  .question-app blockquote span p {
    margin-bottom: 0;
  }

  .question-app .feedback-blockquote {
    padding: 0 0 0 24px;
    margin: 5px 5px 5px 4px;
  }

  .question-app .q-radio blockquote {
    top: 0;
  }


  .question-app .audio-recorder,
  .question-app .media-uploader {
    margin-top: 8px;
  }

  .question-app blockquote.short-answer:not(:last-of-type) {
    margin-bottom: 5px;
  }

  .question-app .free-response-text {
    margin: 1rem 0;
    max-height: 400px;
    overflow-y: auto;
    word-break: break-word;
  }

  .question-app .embed-chip .chip_content {
    cursor: pointer;
  }

  .question-app .q-response .input-group {
    padding: 18px 0 0;
  }

  .question-app .q-response textarea,
  .question-app .q-explanation textarea {
    border-bottom: 1px solid #9e9e9e
  }

  .question-app .q-discussion {
    overflow-y: auto;
    display: flex;
    flex-direction: column;
  }

  .question-app input[type=text]:not(.browser-default):disabled {
    border-bottom-style: solid !important;
  }

  .question-app .time-progress {
    height: 4px !important;
    margin: 0 !important;
    position: absolute;
  }

  .question-app .time-progress div{
    transition: opacity.3s linear;
  }

  .q-checkbox .v-messages {display: none;}

  .question-app input.fill-blank-question {
    display: inline-block;
    border: none;
    border-bottom: 1px solid #9e9e9e;
    border-radius: 0;
    outline: none;
    padding: 0;
    box-shadow: none;
    box-sizing: content-box;
    /*transition: all .3s;*/
    margin-bottom: 10px;
  }

  .question-app .tinymce-disabled {
    pointer-events: none;
    opacity: 0.6;
  }

  .question-app input.fill-blank-question:focus {
    outline: none;
    border-bottom: 2px solid #FFC107;
  }

  .question-app .input-group label {
    height: auto;
  }

  .question-app .input-group.input-group--selection-controls .input-group__input {
    position: absolute;
  }

  .question-app .input-group.input-group--selection-controls label {
    position: relative;
    overflow: visible;
    white-space: normal;
  }

  .question-app .q-footer {
    display: flex;
    justify-content: flex-end;
  }

  .question-app .q.portrait {
    padding-top: 15px;
  }

  .q .v-input--radio-group .v-radio,
  .q .v-input--selection-controls__input
  {
    margin-bottom: 0 !important;
  }
  q-checkbox .v-input--selection-controls .v-input__slot {
    margin-top: 0;
  }

  .question-app .q-footer.mobile {
    flex-direction: column !important;
    justify-content: center !important;
    align-items: center !important;
  }

  .question-app .input-group__details {
    display: none;
  }

  .question-app fieldset {
    padding: 0;
  }

  .question-app .hotspot-hover:hover{
  	box-shadow: 0 0 7px #ddd;background: rgba(21,21,21,.8);
  }

  .question-app img.responsive {
    max-width: calc(100% - 10px);
    margin-left: 5px;
    height: auto;
  }




  .question-app img.hotspot-hover:hover {
	  transform: scale(1.3);
    transition:.2s
  }

  .pp-interaction-title:after {
    content:".";
    display:block;
    height:0;
    clear:both;
    visibility:hidden;
  }

/* [ ] Styling for demo, probably should remove*/
.hs-demo {
	text-align: center;
  color: white;
  cursor: pointer;
}
.hs-demo .material-icons{
    font-size: 8vmin;
}
.hs-demo .hs-title {
  font-size: 3vmin;
  font-weight: 600;
  border-bottom: solid;
  text-transform: uppercase;
  text-align: center;
}
.hs-demo .hs-subtitle {
	font-size: 3vmin;
  font-weight: 300;
  text-transform: none;
  text-align: center;
}

.alert {
  white-space: normal;
}

.scroll-bottom-block {
  position: absolute;
  bottom: 0;
  background: var(--branding-active-hex);
  color: white;
  text-align: center;
  padding: 5px 0;
  width: 100%;
  opacity: 0;
  pointer-events: none;
  transition: all 0.1s linear;
}
.scroll-bottom-block.enabled {
    opacity: .5;
    /*pointer-events: all;*/
}
/*.scroll-bottom-block:hover {*/
/*      text-decoration: underline;*/
/*      cursor: pointer;*/
/*}*/
.v-list-tile {
  padding: 0 1rem;
}
.v-list-tile-title {
  font-weight: 500;
  padding: 3px 16px;
}
.v-list-tile-subtitle {
  font-weight: 400;
  padding: 4px 16px;
}
.v-list-tile-subtitle p:last-child {
  margin-bottom: 0;
  padding: 0px;
}
.v-subheader { margin-left: 0 !important; }

.floating-btn.v-btn {
  align-items: center;
  border-radius: 2px;
  display: inline-flex;
  font-weight: 400;
  justify-content: center;
  outline: 0;
  text-transform: uppercase;
  text-decoration: none;
  transition: .3s cubic-bezier(.25,.8,.5,1),color 1ms;
  vertical-align: middle;
  border: 1px solid;
  box-shadow: none;
  min-width: 88px;
  user-select: none;
  background: white !important;
  z-index: 1001;
}

.floating-btn.v-btn :hover {
  box-shadow: none;
}

.sort-answer-response-text {
  display: inline-block;
}

  #broadcast-pagination{
   height: 35px;
   display: flex;
   flex-flow: row;
   font-family: Roboto, sans-serif;
   justify-content: center;
   align-items: center;
  }
  
  .el-select-dropdown{
    font-family: Roboto, sans-serif;
  }
  
  .loader{
    display: flex;
    flex-flow: row;
    justify-content: center;
    align-items: center;
    vertical-align: middle;
  }

/*
  Broadcast pagination css start
*/

.el-pagination .btn-next,.el-pagination .btn-prev {
	background: center center no-repeat #FFF;
	background-size: 16px;
	cursor: pointer;
	margin: 0;
	color: #303133;
}


.el-pagination .btn-next .el-icon,.el-pagination .btn-prev .el-icon {
	display: block;
	font-size: 12px;
	font-weight: 700;
}

.el-pagination .btn-next {
	padding-left: 12px;
}

.el-pagination--small .btn-next,.el-pagination--small .btn-prev,.el-pagination--small .el-pager li,.el-pagination--small .el-pager li.btn-quicknext,.el-pagination--small .el-pager li.btn-quickprev,.el-pagination--small .el-pager li:last-child {
	border-color: transparent;
	font-size: 12px;
	line-height: 22px;
	height: 22px;
	min-width: 22px;
}

.el-pager,.el-pagination.is-background .btn-next,.el-pagination.is-background .btn-prev {
	padding: 0;
}

.el-pagination.is-background .btn-next,.el-pagination.is-background .btn-prev,.el-pagination.is-background .el-pager li {
	margin: 0 5px;
	background-color: #f4f4f5;
	color: #606266;
	min-width: 30px;
	border-radius: 2px;
}

.el-pagination.is-background .btn-next.disabled,.el-pagination.is-background .btn-next:disabled,.el-pagination.is-background .btn-prev.disabled,.el-pagination.is-background .btn-prev:disabled,.el-pagination.is-background .el-pager li.disabled {
	color: #C0C4CC;
}

.el-pagination.is-background.el-pagination--small .btn-next,.el-pagination.is-background.el-pagination--small .btn-prev,.el-pagination.is-background.el-pagination--small .el-pager li {
	margin: 0 3px;
	min-width: 22px;
}

.el-icon-arrow-right:before {
	content: "\e6e0";
}

.el-date-range-picker__time-header>.el-icon-arrow-right {
	font-size: 20px;
	vertical-align: middle;
	display: table-cell;
	color: #303133;
}

[class*=" el-icon-"],[class^=el-icon-] {
	font-family: element-icons!important;
	speak: none;
	font-style: normal;
	font-weight: 400;
	font-variant: normal;
	text-transform: none;
	line-height: 1;
	vertical-align: baseline;
	display: inline-block;
	-webkit-font-smoothing: antialiased;
	-moz-osx-font-smoothing: grayscale;
}

.el-input__suffix,.el-tree.is-dragging .el-tree-node__content * {
	pointer-events: none;
}

.el-pagination .el-input__suffix {
	right: 0;
	-webkit-transform: scale(.8);
	transform: scale(.8);
}

.el-input__prefix,.el-input__suffix {
	position: absolute;
	top: 0;
	-webkit-transition: all .3s;
	height: 100%;
	color: #C0C4CC;
	text-align: center;
}

.el-input__suffix {
	right: 5px;
	transition: all .3s;
}

.el-input__suffix-inner {
	pointer-events: all;
}

.el-input.is-exceed .el-input__suffix .el-input__count {
	color: #F56C6C;
}

.el-pagination .btn-next,.el-pagination .btn-prev {
	background: center center no-repeat #FFF;
	background-size: 16px;
	cursor: pointer;
	margin: 0;
	color: #303133;
}

.el-pagination .btn-prev {
	padding-right: 12px;
}

.el-pagination .btn-next {
	padding-left: 12px;
}

.el-pagination--small .btn-next,.el-pagination--small .btn-prev,.el-pagination--small .el-pager li,.el-pagination--small .el-pager li.btn-quicknext,.el-pagination--small .el-pager li.btn-quickprev,.el-pagination--small .el-pager li:last-child {
	border-color: transparent;
	font-size: 12px;
	line-height: 22px;
	height: 22px;
	min-width: 22px;
}

.el-pager,.el-pagination.is-background .btn-next,.el-pagination.is-background .btn-prev {
	padding: 0;
}


.el-pagination.is-background .btn-next.disabled,.el-pagination.is-background .btn-next:disabled,.el-pagination.is-background .btn-prev.disabled,.el-pagination.is-background .btn-prev:disabled,.el-pagination.is-background .el-pager li.disabled {
	color: #C0C4CC;
}

.el-icon-arrow-left:before {
	content: "\e6de";
}

.el-icon-arrow-up:before {
	content: "\e6e1";
}

.el-time-spinner__arrow.el-icon-arrow-up {
	top: 10px;
}

.free-response-unanswered {
  display: flex;
  justify-content: flex-start;
}

.auto-height-list-tile .v-list__tile {
  height: auto !important;
}
</style>
